<script>
  import "@urbaninstitute/dataviz-components/style"; // import _normalize.css and _typography.css (urban-specific classes)
  import { Meta, Block } from "@urbaninstitute/dataviz-components";
  import { getAbsoluteUrl } from "$lib/utils/urls.js";
  import states from "$data/states.json";
  import InfoTable from "$lib/InfoTable.svelte";
  import data from "$data/office-data.csv";
  import DetailHeader from "$lib/DetailHeader.svelte";
  import { preciseFormat, roundFormat } from "$utils/formats.js";

  let dropdownList = $derived(states.map((d) => ({ label: d.state, value: d.fips })));
  let currentState = $state("01");
  let currentStateName = $derived(dropdownList.find((d) => d.value == currentState).label);

  let infoData = $derived(
    data
      ? data
          .filter((d) => d.state_fips_served == currentState)
          .map((d) => ({ ...d, f_address: d.f_address.replace(", ,", ",") }))
          .sort((a, b) => b.population_2023 - a.population_2023)
      : []
  );

  let tableColumnLabels = $derived([
    "SSA offices covering " + currentStateName,
    "Congressional districts the office currently covers",
    "Total population office currently covers ↓",
    "People ages 65 and older office currently covers",
    "Disabled people office currently covers",
    "Average change in drive time to the next closest office if the office closed (minutes)"
  ]);
  let colFormats = [null, null, roundFormat, roundFormat, roundFormat, preciseFormat];
</script>

<!-- set robotsNoIndex to true for an embed, leave as false for a full page app-->
<!-- <Meta
  title="DataViz@URBAN"
  description="SvelteKit-powered starter template for data viz projects"
  publishDate="2024-01-01"
  robotsNoIndex={true}
  socialImage={getAbsoluteUrl("social.jpg")}
/> -->

<Block>
  <h2 class="tool-header">Explore Your Community’s Access to SSA Offices</h2>
  <p>
    Select a state below to explore how drive time to the closest SSA office varies across the
    state.
  </p>
  <p>
    On the map, pink dots represent Social Security Administration offices. Census tracts are shaded
    in blue based on how long people in the tract have to drive to reach their closest SSA office.
    Offices can be located outside the state because SSA offices serve all people, regardless of
    where they reside.
  </p>
  <div class="inline">
    <h4>How long it takes people in</h4>
    <DetailHeader stateDropdownData={dropdownList} bind:currentState />
    <h4>to drive to their closest SSA office</h4>
  </div>

  <img
    class="map"
    width="100%"
    src={getAbsoluteUrl("/maps/" + currentState + ".svg")}
    alt="A map of a state"
  />
  <p class="source-notes small">
    <strong>Note:</strong> Census tracts shaded in gray represent areas that aren't connected by road
    to an SSA office. Tracts shaded in white are areas with no population.
  </p>
</Block>
<Block width="wide">
  <h4 class="section-title">How an SSA office closure could affect people in {currentStateName}</h4>
  <InfoTable
    data={infoData}
    colLabels={tableColumnLabels}
    {colFormats}
    cols={[
      "f_address",
      "congressional_districts_covered",
      "population_2023",
      "population_2023_65_plus",
      "disabled_2023",
      "duration_change"
    ]}
  />
</Block>

<style>
  h2.tool-header {
    @media screen and (max-width: 770px) {
      font-size: var(--font-size-3-5xl) !important;
    }
  }
  .inline {
    display: flex;
    flex-wrap: wrap;
    gap: 0 15px;
    justify-content: center;
    margin-top: 40px;
    h4 {
      margin: 0;
    }
  }
  h4.section-title {
    text-align: center;
    margin-top: 40px;
  }
  img.map {
    margin-top: 20px;
  }
  p.source-notes.small {
    font-size: var(--font-size-small);
    line-height: var(--line-height-snug);
    margin: 10px 0 20px 0;
  }
</style>
